import { vb360Api } from "@/features/vb360/api/vb360.api";
import type { Vb360ContextDto } from "@/features/vb360/types/vb360.types";

export type Vb360ContextStatus = "idle" | "loading" | "ready" | "forbidden" | "unavailable";

export type Vb360ContextState = {
    userId: string;
    status: Vb360ContextStatus;
    context: Vb360ContextDto | null;
};

const initialState: Vb360ContextState = { userId: "", status: "idle", context: null };

let state: Vb360ContextState = initialState;
const listeners = new Set<() => void>();

const publish = (next: Vb360ContextState): void => {
    state = next;
    listeners.forEach((listener) => listener());
};

const getState = (): Vb360ContextState => state;

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const reset = (): void => publish(initialState);

const deny = (): void => publish({ userId: state.userId, status: "forbidden", context: null });

const load = async (userId: string): Promise<void> => {
    if (state.userId === userId && state.status !== "idle") return;
    publish({ userId, status: "loading", context: null });
    try {
        const context = await vb360Api.getContext();
        if (state.userId !== userId) return;
        publish({ userId, status: "ready", context });
    } catch (error) {
        if (state.userId !== userId) return;
        publish({ userId, status: vb360Api.failureOf(error).kind === "forbidden" ? "forbidden" : "unavailable", context: null });
    }
};

export const vb360ContextStore = { getState, subscribe, load, deny, reset };
